/**
 * What the document renderer needs to draw comment highlights, shared through
 * context so the recursive Block -> Inlines -> Inline -> Marked chain does not
 * thread three more props through every level.
 *
 * DocView owns one of these. It writes `ranges` whenever the blocks or the
 * comment list change, and `active` when a thread is hovered or focused (from
 * either side). The renderer only reads, and calls `activate` when a highlight
 * or its pin is chosen.
 */

import { getContext, setContext } from 'svelte'
import { coverOfThreads, gapKey, type BlockRange, type Cover, type GapThread } from './anchors'

const KEY = Symbol('comment-highlights')

export class Highlights {
  /** Each block's live ranges, in raw offsets. See lib/anchors.ts. */
  ranges = $state<Map<string, BlockRange[]>>(new Map())
  /** Gap threads (TASK-044), by `gapKey(after, before)`. See lib/anchors.ts. */
  gaps = $state<Map<string, GapThread[]>>(new Map())
  /** The thread whose range is lit up, if any. */
  active = $state<string | null>(null)

  constructor(
    /** A highlight or pin was chosen: focus that thread in the sidebar. */
    readonly activate: (threadId: string) => void,
  ) {}

  /**
   * The ranges to DRAW in one block. A closed thread is drawn only while it is
   * the active one -- hover or focus it in the sidebar and its words light up;
   * otherwise its highlight is hidden, as the brief for closed threads asks.
   */
  rangesFor(blockId: string | undefined): BlockRange[] {
    if (!blockId) return []
    const all = this.ranges.get(blockId)
    if (!all) return []
    return all.filter((r) => r.state !== 'closed' || r.threadId === this.active)
  }

  /**
   * The marker to DRAW in one gap, if any: its threads and their most
   * prominent state, a closed thread only while it is the active one -- the
   * same rule as a text highlight.
   */
  gapCover(after: string | null, before: string | null): (Cover & { labels: string[] }) | null {
    const all = this.gaps.get(gapKey(after, before))
    if (!all) return null
    const shown = all.filter((t) => t.state !== 'closed' || t.threadId === this.active)
    const cover = coverOfThreads(shown)
    return cover ? { ...cover, labels: shown.map((t) => t.label) } : null
  }
}

export function provideHighlights(h: Highlights): void {
  setContext(KEY, h)
}

/** Null outside a DocView (the renderer is also usable without comments). */
export function useHighlights(): Highlights | null {
  return getContext<Highlights | undefined>(KEY) ?? null
}
